/**
 * Phone normalisation to E.164 — AGENTS.md §5.
 *
 * Every phone number is normalised at the boundary before it reaches the database.
 * `Customer.phoneE164` is the platform-wide identity key, so two spellings of the
 * same number must collapse to one string or we create two humans.
 *
 * Lebanon is the launch market and gets full national-format handling, but the
 * default country is a parameter, never a hard-coded assumption (AGENTS.md §10).
 * Numbers already in international form are accepted for any country.
 */
export class PhoneParseError extends Error {
    reason;
    constructor(reason) {
        super(`Phone number could not be parsed: ${reason}`);
        this.reason = reason;
        this.name = 'PhoneParseError';
    }
}
/**
 * Lebanese national significant number (NSN) prefixes and their exact total NSN
 * length. The trunk `0` is not part of the NSN and is stripped before matching.
 *
 * Mobile `3` is 7 digits (3 + 6); mobile `70`–`81` are 8 digits (2 + 6). Length is
 * what disambiguates mobile `70XXXXXX` from South Lebanon landline `7XXXXXX`.
 */
const LB_NSN_RULES = [
    { prefix: '3', length: 7, mobile: true },
    { prefix: '70', length: 8, mobile: true },
    { prefix: '71', length: 8, mobile: true },
    { prefix: '76', length: 8, mobile: true },
    { prefix: '78', length: 8, mobile: true },
    { prefix: '79', length: 8, mobile: true },
    { prefix: '81', length: 8, mobile: true },
    // Landlines, by region.
    { prefix: '1', length: 7, mobile: false },
    { prefix: '4', length: 7, mobile: false },
    { prefix: '5', length: 7, mobile: false },
    { prefix: '6', length: 7, mobile: false },
    { prefix: '7', length: 7, mobile: false },
    { prefix: '8', length: 7, mobile: false },
    { prefix: '9', length: 7, mobile: false },
];
const LB_DIAL_CODE = '961';
/**
 * Strips spaces, dashes, dots and parentheses down to digits.
 *
 * The plus is matched anywhere ahead of the first digit, not just at position 0.
 * A country selector rendering its value as `(+33)`, or a number pasted from a
 * contact card, would otherwise lose the international marker and be reparsed as
 * a Lebanese national number.
 */
function scrub(raw) {
    const trimmed = raw.trim();
    const hadPlus = /^[^\d]*\+/.test(trimmed);
    const digits = trimmed.replace(/\D/g, '');
    return { digits, hadPlus };
}
/**
 * Longest prefix first, so `70` is tested before `7` and the length check
 * disambiguates. Sorted once at module load — this runs on the counter lookup
 * path, which has a 200 ms p95 budget (ARCHITECTURE.md §13).
 */
const LB_RULES_BY_SPECIFICITY = [...LB_NSN_RULES].sort((a, b) => b.prefix.length - a.prefix.length);
function matchLebanon(nsn) {
    for (const rule of LB_RULES_BY_SPECIFICITY) {
        if (nsn.startsWith(rule.prefix) && nsn.length === rule.length) {
            return { e164: `+${LB_DIAL_CODE}${nsn}`, isMobile: rule.mobile };
        }
    }
    // Distinguish "we know this prefix but the length is wrong" from "no such prefix",
    // because the two produce different validation messages for the user.
    const prefixKnown = LB_NSN_RULES.some((r) => nsn.startsWith(r.prefix));
    throw new PhoneParseError(prefixKnown ? 'BAD_LENGTH' : 'UNKNOWN_PREFIX');
}
/** A conservative generic check for numbers that are already international. */
function matchInternational(digits) {
    if (digits.length < 8 || digits.length > 15)
        throw new PhoneParseError('BAD_LENGTH');
    if (digits.startsWith('0'))
        throw new PhoneParseError('NOT_A_NUMBER');
    // Outside the launch market we cannot tell mobile from landline without a full
    // numbering-plan database, so we do not guess.
    return { e164: `+${digits}`, isMobile: false };
}
/**
 * Normalises user input to E.164. Throws `PhoneParseError` on anything unparseable —
 * callers at an API boundary should translate that into `VALIDATION_FAILED`.
 */
export function normalizePhone(input, defaultCountry = 'LB') {
    if (!input || !input.trim())
        throw new PhoneParseError('EMPTY');
    const { digits, hadPlus } = scrub(input);
    if (!digits)
        throw new PhoneParseError('NOT_A_NUMBER');
    // 00 is the international access prefix; treat it exactly like a leading +.
    const international = hadPlus || digits.startsWith('00');
    const stripped = digits.startsWith('00') ? digits.slice(2) : digits;
    if (stripped.startsWith(LB_DIAL_CODE)) {
        const nsn = stripped.slice(LB_DIAL_CODE.length);
        // A bare local number that happens to begin with 961 is not a country code.
        if (nsn.length > 0)
            return matchLebanon(nsn);
    }
    if (international)
        return matchInternational(stripped);
    if (defaultCountry === 'LB') {
        // National format: the trunk 0 is dropped for E.164.
        const nsn = stripped.startsWith('0') ? stripped.slice(1) : stripped;
        return matchLebanon(nsn);
    }
    throw new PhoneParseError('UNKNOWN_PREFIX');
}
/** Non-throwing variant for form-level validation. */
export function tryNormalizePhone(input, defaultCountry = 'LB') {
    try {
        return normalizePhone(input, defaultCountry);
    }
    catch {
        return null;
    }
}
/**
 * Masks a stored E.164 number for display to staff — API-CONTRACT.md §3 shows
 * `+9617•••4567`. Staff need enough to confirm identity aloud, not the whole number.
 */
export function maskPhone(e164) {
    if (!e164.startsWith('+') || e164.length < 8)
        return '•••';
    const head = e164.slice(0, 5);
    const tail = e164.slice(-4);
    return `${head}•••${tail}`;
}
//# sourceMappingURL=phone.js.map